import React, { useState } from 'react';

import {
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  useMantineTheme,
  Box,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconDots } from '@tabler/icons-react';
import RegionSelect from 'components/form-fields/auto-complete-fields/RegionSelect';
import PhoneInput from 'components/form-fields/input-fields/PhoneInput';
import PostalCodeInput from 'components/form-fields/input-fields/PostalCodeInput';
import VerifiedPhoneInput from 'components/form-fields/input-fields/VerifiedPhoneInput';
import CountrySelect from 'components/form-fields/select-fields/CountrySelect';
import GenderSelect from 'components/form-fields/select-fields/GenderSelect';
import HelpButton from 'components/HelpButton';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useSelfAthlete from 'hooks/athlete/useSelfAthlete';
import useUpdateSelfAthlete from 'hooks/athlete/useUpdateSelfAthlete';
import { Country, CountryEnum } from 'types/enums';
import { emailValidator, nameValidator, phoneValidator } from 'utils/form-validators';
import { formatPhone } from 'utils/formatters';
import notificationHandler from 'utils/notifications';

interface FormValues {
  athleteId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  dob: Date | null;
  gender:
    | 'Male'
    | 'Female'
    | 'Transgender'
    | 'Non-binary/Non-conforming'
    | 'Prefer not to respond'
    | ''
    | null;
  address: {
    addressLines: string | null;
    locality: string | null;
    region: string | null;
    zipCode: string | null;
    country: Country;
  };
  emergencyName: string | null;
  emergencyPhone: string;
  medicalNotes: string | null;
}

const ATHLETE_ACCOUNT_FORM_ID = 'athlete-account-form';

const AthleteAccountForm = (): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();

  const [isEditable, setIsEditable] = useState(false);

  const { successNotification, errorNotification } = notificationHandler();
  const updateMutation = useUpdateSelfAthlete();

  const { data, isLoading, isSuccess } = useSelfAthlete();

  const form = useForm<FormValues>({
    initialValues: {
      athleteId: data?.data.athleteId || '',
      email: data?.data.email || '',
      firstName: data?.data?.firstName || '',
      lastName: data?.data.lastName || '',
      phone: data?.data.phone || '',
      dob: data?.data.dob ? dayjs(data?.data.dob).toDate() : null,
      gender: data?.data.gender || null,
      address: {
        addressLines: data?.data.address.addressLines || '',
        locality: data?.data.address.locality || '',
        region: data?.data.address.region || '',
        zipCode: data?.data.address.zipCode || '',
        country: data?.data.address.country || CountryEnum.USA,
      },
      emergencyName: data?.data.emergencyName || '',
      emergencyPhone: data?.data.emergencyPhone || '',
      medicalNotes: data?.data.medicalNotes || '',
    },

    validate: {
      firstName: value => nameValidator(value),
      lastName: value => nameValidator(value),
      email: value => emailValidator(value),
      phone: value => (value ? phoneValidator(value) : null),
      emergencyPhone: value => (value ? phoneValidator(value) : null),
    },
    transformValues: (values: FormValues): FormValues => ({
      ...values,
      phone: formatPhone(values.phone),
      emergencyPhone: formatPhone(values.emergencyPhone),
      address: {
        ...values.address,
        zipCode: values.address.zipCode?.toUpperCase() || null,
      },
    }),
  });

  const handleSetForm = (): void => {
    if (isSuccess && data?.data) {
      form.setValues({
        ...data.data,
        dob: data.data.dob ? dayjs(data.data.dob).toDate() : null,
        phone: data.data.phone ?? '',
        emergencyPhone: data.data.emergencyPhone ?? '',
      });
      form.resetDirty();
    }
  };

  const handleFormReset = (): void => {
    handleSetForm();
    setIsEditable(false);
  };

  const handleSubmit = (values: FormValues): void => {
    if (isSuccess && data?.data) {
      updateMutation.mutate(
        {
          ...data.data,
          ...values,
          dob: values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : null,
          phone: values.phone.length > 10 ? values.phone : '',
          gender: values.gender ?? null,
          emergencyName: values.emergencyName ?? null,
          emergencyPhone: values.emergencyPhone.length > 10 ? values.emergencyPhone : '',
          medicalNotes: values.medicalNotes ?? null,
        },
        {
          onSuccess: () => {
            successNotification('Account details updated successfully');
            handleFormReset();
          },
          onError: error => {
            errorNotification('Account details update failed', error.response?.data.message);
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    handleSetForm();
  }, [data?.data, isSuccess]);

  return (
    <Paper radius='lg' p='md' shadow='md' withBorder>
      <Group position='apart' pb='md'>
        <Title order={4} color='dark.3'>
          General Information
        </Title>
        <Group position='right'>
          {isEditable ? (
            <>
              <Button
                variant='light'
                compact
                color='red'
                onClick={handleFormReset}
                disabled={updateMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                form={ATHLETE_ACCOUNT_FORM_ID}
                compact
                color='green'
                loading={updateMutation.isLoading}
              >
                Save
              </Button>
            </>
          ) : (
            <Button compact onClick={() => setIsEditable(true)}>
              Edit
            </Button>
          )}
        </Group>
      </Group>
      <form id={ATHLETE_ACCOUNT_FORM_ID} onSubmit={form.onSubmit(handleSubmit)}>
        <LoadingOverlay visible={isLoading} />
        <Stack spacing='xs' p='xs'>
          <Group grow spacing='xs'>
            <TextInput
              {...form.getInputProps('firstName')}
              label='First Name'
              placeholder='Enter First Name'
              required={isEditable}
              disabled={!isEditable}
            />
            <TextInput
              {...form.getInputProps('lastName')}
              label='Last Name'
              placeholder='Enter Last Name'
              required={isEditable}
              disabled={!isEditable}
            />
          </Group>
          <Group>
            <TextInput
              {...form.getInputProps('email')}
              type='email'
              label='Email / Login'
              placeholder='Enter Email'
              disabled
              sx={{ flexGrow: 1 }}
            />
            {isEditable && (
              <Box pt='20px'>
                <HelpButton width={'md'} position='top-start' withinPortal>
                  <Text size='sm' sx={{ whiteSpace: 'normal' }}>
                    {`To change emails use the `}
                    <Text fw={600} span>
                      Edit Login
                    </Text>
                    {` button in your profile selection page.`}
                  </Text>
                </HelpButton>
              </Box>
            )}
          </Group>
          {!isEditable ? (
            <Group position='center'>
              <IconDots color={theme.colors.gray[5]} />
            </Group>
          ) : (
            <>
              <VerifiedPhoneInput
                country={form.values.address.country}
                error={form.errors.phone}
                value={form.values.phone || ''}
                onChange={value => form.setFieldValue('phone', value)}
              />
              <Group grow spacing='xs'>
                <DateInput
                  {...form.getInputProps('dob')}
                  label='Date of Birth'
                  placeholder={dateFormat}
                  valueFormat={dateFormat}
                  clearable
                  defaultLevel='decade'
                  disabled={!isEditable}
                  maxDate={dayjs().toDate()}
                  firstDayOfWeek={0}
                />
                <GenderSelect {...form.getInputProps('gender')} disabled={!isEditable} />
              </Group>
              <TextInput
                {...form.getInputProps('address.addressLines')}
                type='text'
                label='Address'
                placeholder='Enter Address'
                disabled={!isEditable}
              />
              <TextInput
                {...form.getInputProps('address.locality')}
                type='text'
                label='City'
                placeholder='Enter City'
                disabled={!isEditable}
              />
              <Group grow spacing='xs'>
                <RegionSelect
                  country={form.values.address.country}
                  {...form.getInputProps('address.region')}
                  clearable
                  disabled={!isEditable}
                />
                <PostalCodeInput
                  {...form.getInputProps('address.zipCode')}
                  country={form.values.address.country}
                  disabled={!isEditable}
                  formErrors={form.errors.postalCode}
                />
                <CountrySelect
                  label='Country'
                  value={form.values.address.country}
                  onChange={value => {
                    form.setFieldValue('address.region', '');
                    form.setFieldValue('address.zipCode', '');
                    form.setFieldValue('address.country', value);
                  }}
                  disabled={!isEditable}
                />
              </Group>
              <TextInput
                {...form.getInputProps('emergencyName')}
                type='text'
                label='Emergency Contact'
                placeholder='Enter Full Name'
                disabled={!isEditable}
              />
              <PhoneInput
                {...form.getInputProps('emergencyPhone')}
                label='Emergency Phone'
                onChange={value => form.setFieldValue('emergencyPhone', value)}
                placeholder='Enter Phone Number'
                disabled={!isEditable}
              />
              <Textarea
                {...form.getInputProps('medicalNotes')}
                label='Medical Notes'
                placeholder='Enter Medical Notes'
                disabled={!isEditable}
              />
            </>
          )}
        </Stack>
      </form>
    </Paper>
  );
};

export default AthleteAccountForm;
